<script setup lang="ts">
/**
 * **JS 节点的特殊设置**（住在「属性」面板里）—— 输入端口表 + 代码。
 *
 * ## 为什么它在这里，而不是在节点上
 *
 * 用户定的分工（原话）：
 *
 * > 节点本身就有控件支持调整数值，属性面板可以留着给节点用于节点的一些特殊的设置。
 *
 * 端口表与代码正好是"特殊设置"：它们**不是**能顺手拖一下的值，
 * 而是一张会增删的表 + 一段代码 —— 挤在节点里既放不下也不好点。
 * 节点上只留真正的输入输出端口（用户原话："JS 代码节点不要显示代码，
 * 这个参数在节点上也不要显示端口的信息，就只显示有哪些输入输出端口就行"）。
 *
 * ## 输出端口**只读**：它由代码的 `return` 决定
 *
 * > 去掉手动添加输出端口的功能，通过检查代码的 return 来判断输出端口有多少个。
 *
 * 所以这里没有"＋ 输出"按钮，只有一份**实时预览**：改代码的同时就能看到
 * 会长出哪几个端口（真正确认要等 `change`，见下面那条"边打边存"的说明）。
 *
 * ## 它改的是**文档里的参数**（`ports` / `code` 两行）
 *
 * ⚠️ 所以每一次增删改都要走 `setValue`（宿主的历史与校验）——
 * 不要直接改 model：那样撤销记不住、别的窗口也不会知道。
 *
 * ## ★★ 代码是**边打边存**的（这条修的是一个真 bug）
 *
 * 用户原话："我在属性面板修改了代码之后，它没有存储，又给我替换成默认的"。
 * 原因是原来只听 `change`（失焦那一刻），而文本框里的内容在那之前
 * 只活在 DOM 里 —— 只要失焦没发生（点画布、切窗口、面板重渲染），
 * 写进去的字就丢了，界面重新绑回文档里的旧值（看起来就是"被换回默认"）。
 *
 * 现在两拍：
 *   · `input`（每敲一个字符）→ **立刻写进文档**，一个字都不会丢；
 *   · `change`（失焦）→ 顺手**重建行**（输出端口按新代码长出来）。
 *
 * ⚠️ 重建**不能**跟着每次按键做：中途的半截代码（`return {`）会把端口
 * 暂时推成"一个「结果」"，那会顺手删掉挂在 `out1` 上的线 —— 用户只是
 * 打字打到一半，线却没了。所以行只在编辑结束那一拍重建。
 */
import { computed, onBeforeUnmount } from 'vue'
import type { GraphNodeModel, NodeRow } from '@/types/node'
import type { Widget } from '@/types/widget'
import { JS_PORT_TYPES, parsePortsConfig, serializePortsConfig, tryParsePortsConfig, type JsPortsConfig } from '@/node/jsNodeType'
import { declarationProblems, deriveOutputPorts, type JsOutputPort } from '@/node/jsRuntime'
import type { SocketType } from '@/types/socket'

const props = defineProps<{
  /** 目标节点（JS 节点） */
  model: GraphNodeModel
  /**
   * 写一个参数值（走宿主：历史 + 校验 + 通知）。
   *
   * `options.rebuild` = 这次改动要不要顺手重建节点的行（端口）。
   * 打字过程中**不要**重建（见文件头那段）。
   */
  setValue: (rowId: string, value: unknown, options?: { rebuild?: boolean }) => void
  /** 参数被改过的版本号（见上面那段说明：不读它，面板不会更新） */
  editVersion?: number
}>()

/**
 * 取某个属性行的控件（值就是它的 `value`）。
 *
 * ⚠️ **先摸一下 `props.editVersion`**：节点模型里的控件值是普通对象的字段，
 * 只有"宿主那条响应式链"才知道它变了。`model` 在真实宿主里恰好是响应式的
 * （Vue Flow 的 nodes 是深响应），但**不能靠这个巧合** —— 组件层单测里
 * 传进来的就是普通对象，那时少了这一行，读到的值会**永远停在第一次**
 * （表现就是"改了代码，面板和节点都没反应"）。这一行让重算由版本号驱动，
 * 和 model 是不是响应式无关。
 */
function widgetOf(rowId: string): Widget | undefined {
  void props.editVersion
  const row: NodeRow | undefined = props.model.rows.find((item) => item.id === rowId)
  return row && (row.kind === 'property' || row.kind === 'input') ? row.widget : undefined
}

/*
 * ⚠️ 读 `model.rows` 得**经过反应式的东西**：宿主每次写参数会 bump 一个版本号，
 * 但那个版本号在宿主组件里 —— 这里靠"父组件重渲染时把新 model 传下来"。
 * 所以 `props.model` 的引用必须是新的……它其实**不是**（同一个对象）。
 * 真正起作用的是父组件在版本号变化时重渲染，并把同一个 model 再传一次 ——
 * Vue 会发现 prop 没变、跳过子组件更新 ✗。所以这里也读一下 `props.editVersion`
 * （`widgetOf` 里那一次就够，这里再显式读一次是为了让依赖一目了然）。
 */
const ports = computed(() => {
  void props.editVersion
  return parsePortsConfig(widgetOf('ports')?.value)
})
const code = computed(() => String(widgetOf('code')?.value ?? ''))

/** 代码里的 `return` 推出来的输出端口（**实时**，还没落进节点） */
const derived = computed<JsOutputPort[]>(() => {
  void props.editVersion
  return deriveOutputPorts(code.value)
})

/** 宿主的输入声明校验（名字非法 / 重名 / 保留名）—— 面板上原样转述 */
const inputProblems = computed(() => declarationProblems({ inputs: ports.value.inputs, code: code.value }))

/**
 * 端口声明那段 JSON **读不出来**。
 *
 * ⚠️ 必须说出来：读不出来时宿主会**保住旧的行**（不重建，见 `rebuildJsRows`），
 * 于是面板上看到的输入和节点上的端口可能对不上 —— 不说的话，用户会以为
 * "改端口没反应"。
 */
const portsBroken = computed(() => {
  void props.editVersion
  return tryParsePortsConfig(widgetOf('ports')?.value) === null
})

/** 哪几个名字现在是错的（用来把那一行标红） */
const badNames = computed(() => new Set(inputProblems.value.map((item) => item.name ?? '')))

/**
 * 节点上**现在**挂着的输出端口标签。
 *
 * 拿它和 `derived` 比：不一样就说明"代码改了、行还没重建" —— 那正是
 * 用户报的那件事（"改成了数组，端口没多出来"），所以要**说出来**，
 * 而不是让他盯着一个没反应的节点。
 */
const nodeOutputLabels = computed(() => {
  void props.editVersion
  return props.model.rows.filter((row) => row.kind === 'output').map((row) => row.label ?? '')
})
const portsStale = computed(
  () => JSON.stringify(nodeOutputLabels.value) !== JSON.stringify(derived.value.map((port) => port.label)),
)

/**
 * 输出那一段的解释（三种形态各说一句，别让用户猜端口是怎么来的）。
 *
 * ⚠️ 这里的文案是**纯文本**（不解析 markdown）：不写 `**粗体**`、
 * 也不写反引号 —— 它们在界面上会原样显示成一个星号和一对反引号
 * （上一版就是这么把 `**数组**` 印在面板上的）。
 */
const outputNote = computed(() => {
  const first = derived.value[0]
  if (!first) return ''
  if (first.kind === 'whole') {
    return '没能从代码里看出结构（比如 return 的是一个变量）。这种情况整份结果从「结果」端口出去 —— 想要多个端口，就 return 一个对象：return { 名字: 值 }。'
  }
  if (first.kind === 'index') {
    return 'return 的是数组，所以按位置给端口：return [a, b] 就是 0 / 1 两路。'
  }
  if (derived.value.length === 1) {
    return 'return 的对象只有一个键，所以只有一个输出端口。'
  }
  return 'return 的对象有几个键，就有几个输出端口。'
})

/** 写回端口声明（统一走一个出口：面板里所有增删改都过它） */
function writePorts(next: JsPortsConfig): void {
  /* ⚠️ 端口声明变了就要重建行（加一路输入 → 节点上真的要多一个端口） */
  props.setValue('ports', serializePortsConfig(next), { rebuild: true })
}

function addInput(): void {
  const inputs = [...ports.value.inputs]
  /*
   * 名字给个不撞车、而且是**合法标识符**的默认值 —— 代码里要用
   * `inputs.<名字>` 读它（`jsRuntime` 会校验名字合法性）。
   */
  let index = inputs.length
  while (inputs.some((item) => item.name === `in${index}`)) index += 1
  /*
   * ⚠️ 默认类型是 **`any`**（用户明确要求）：代码里的值是什么类型，
   * 静态看不出来（"如果不能直接判断代码的类型，默认都是 any"）。
   * 想要校验就在属性面板里改成具体类型。
   */
  inputs.push({ name: `in${index}`, type: 'any', mode: 'fixed' })
  writePorts({ inputs })
}

function removePort(index: number): void {
  const inputs = [...ports.value.inputs]
  inputs.splice(index, 1)
  writePorts({ inputs })
}

/**
 * 改名。
 *
 * ⚠️ 走 `@input`（每次按键就写回）而不是 `@change` —— 和代码框同一个理由：
 * 只等失焦的话，那些字可能只活在 DOM 里（失焦没发生就没了）。
 *
 * ⚠️ 改名**可以**每次按键都重建行：输入端口的位置 handle 是 `in0` / `in1`
 * （按同类序号发），改名只动**标签**，不会让任何一条线变成悬空边。
 * 变参形态是另一个按钮（`toggleVariadic`），不走这里。
 */
function renamePort(index: number, name: string): void {
  const inputs = [...ports.value.inputs]
  const current = inputs[index]
  if (!current || current.name === name) return
  inputs[index] = { ...current, name }
  writePorts({ inputs })
}

function retypePort(index: number, type: SocketType): void {
  const inputs = [...ports.value.inputs]
  const current = inputs[index]
  if (!current) return
  inputs[index] = { ...current, type }
  writePorts({ inputs })
}

/**
 * 切换"固定参数 / 变参行"。
 *
 * ⚠️ **只允许一组变参**：多组的话代码里会出现两个数组，而它们连的线
 * 在图上分不出谁是谁（`args` 只有一行）。所以打开一个时把其它关掉 ——
 * `rowsForJsNode` 那边也会再归一化一次（两处都做，是因为面板之外的路径
 * 也可能写这份声明）。
 */
function toggleVariadic(index: number): void {
  const inputs = ports.value.inputs.map((item, itemIndex) => {
    const next = item.mode === 'variadic' ? 'fixed' : 'variadic'
    if (itemIndex === index) return { ...item, mode: next as 'fixed' | 'variadic' }
    /* 别的路一律回到"固定参数" */
    return item.mode === 'variadic' ? { ...item, mode: 'fixed' as const } : item
  })
  writePorts({ inputs })
}

/** 每敲一个字符就写进文档（只存值，不重建行 —— 见文件头那段） */
function onCodeInput(event: Event): void {
  props.setValue('code', (event.target as HTMLTextAreaElement).value)
}

/** 编辑告一段落：这时代码是完整的，可以按它重建输出端口了 */
function onCodeChange(event: Event): void {
  props.setValue('code', (event.target as HTMLTextAreaElement).value, { rebuild: true })
}

/**
 * Tab = 两个空格，而不是跳走焦点。
 *
 * ⚠️ 面板里的代码框和节点里那个（`NpWidgetCode`）必须**同一套手感**：
 * 同一个用户在同一份代码上敲 Tab，一边缩进一边跳焦点的话，
 * 他会以为其中一处坏了。
 */
function onCodeKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Tab') return
  event.preventDefault()
  const target = event.target as HTMLTextAreaElement
  const start = target.selectionStart
  const end = target.selectionEnd
  const next = `${target.value.slice(0, start)}  ${target.value.slice(end)}`
  target.value = next
  target.selectionStart = target.selectionEnd = start + 2
  props.setValue('code', next)
}

/*
 * ⚠️ **收起面板时补一次重建**：用户可能敲完代码就直接切走了
 * （切页签 / 关窗口 / 换节点），`change` 那一刻不一定来。
 * 不补的话代码存住了、端口却还是旧的 —— 那正是"改了没反应"的另一半。
 */
onBeforeUnmount(() => {
  if (portsStale.value) props.setValue('code', code.value, { rebuild: true })
})
</script>

<template>
  <div class="np-jsset" data-np-js-settings>
    <p class="np-jsset__lead">
      代码里用 <code>inputs.名字</code> 读输入；<code>return</code> 的对象有几个键，
      节点上就长出几个输出端口。
    </p>

    <!-- ── 输入 ────────────────────────────────────────────── -->
    <section class="np-jsset__card">
      <header class="np-jsset__head">
        <h5 class="np-jsset__title">输入</h5>
        <span class="np-jsset__count">{{ ports.inputs.length }}</span>
        <span class="np-jsset__spacer" />
        <button
          class="np-jsset__add"
          type="button"
          data-np-js-add-input
          title="加一路输入端口"
          @click="addInput"
        >
          ＋ 加一路
        </button>
      </header>

      <p v-if="!ports.inputs.length" class="np-jsset__note">
        还没有输入。加一路之后，代码里就能用 <code>inputs.名字</code> 读它。
      </p>

      <ul v-else class="np-jsset__list">
        <li
          v-for="(port, index) in ports.inputs"
          :key="`in-${index}`"
          class="np-jsset__port"
          :class="{ 'is-bad': badNames.has(port.name), 'is-variadic': port.mode === 'variadic' }"
        >
          <div class="np-jsset__port-main">
            <input
              class="np-jsset__name"
              :value="port.name"
              spellcheck="false"
              autocapitalize="off"
              autocomplete="off"
              :title="`代码里读它：inputs.${port.name}`"
              :data-np-js-name="`in-${index}`"
              @input="renamePort(index, ($event.target as HTMLInputElement).value)"
            />
            <button
              class="np-jsset__icon"
              type="button"
              title="删掉这一路（连着它的线也会一起删）"
              @click="removePort(index)"
            >
              ✕
            </button>
          </div>
          <div class="np-jsset__port-sub">
            <select
              class="np-jsset__select"
              :value="port.type"
              title="端口类型（连线的类型校验用）"
              @change="retypePort(index, ($event.target as HTMLSelectElement).value as SocketType)"
            >
              <option v-for="type in JS_PORT_TYPES" :key="type" :value="type">{{ type }}</option>
            </select>
            <button
              class="np-jsset__mode"
              type="button"
              :class="{ 'is-on': port.mode === 'variadic' }"
              :title="
                port.mode === 'variadic'
                  ? '变参行：一组端口 → 代码里是一个数组'
                  : '固定参数：一路端口 → 代码里是一个值'
              "
              @click="toggleVariadic(index)"
            >
              {{ port.mode === 'variadic' ? '一组 → 数组' : '一路' }}
            </button>
          </div>
        </li>
      </ul>

      <p
        v-for="problem in inputProblems"
        :key="problem.message"
        class="np-jsset__problem"
      >
        {{ problem.message }}
      </p>
      <p v-if="portsBroken" class="np-jsset__problem" data-np-js-ports-broken>
        端口声明读不出来（那段 JSON 坏了）—— 节点上保留的还是上一次能读到的端口。
        重新加一路输入会把它整段覆盖掉。
      </p>
    </section>

    <!-- ── 输出（只读：由代码的 return 决定）────────────────── -->
    <section class="np-jsset__card">
      <header class="np-jsset__head">
        <h5 class="np-jsset__title">输出</h5>
        <span class="np-jsset__count">{{ derived.length }}</span>
        <span class="np-jsset__spacer" />
        <span class="np-jsset__tag">由代码的 return 决定</span>
      </header>

      <ul class="np-jsset__outs" data-np-js-outputs>
        <li v-for="(port, index) in derived" :key="`out-${index}`" class="np-jsset__out">
          <span class="np-jsset__out-name">{{ port.label }}</span>
          <span class="np-jsset__out-kind">{{
            port.kind === 'whole' ? '整体' : port.kind === 'index' ? '下标' : '键'
          }}</span>
        </li>
      </ul>

      <p class="np-jsset__note">{{ outputNote }}</p>
      <p v-if="portsStale" class="np-jsset__pending" data-np-js-ports-stale>
        节点上的端口还是旧的 —— 离开代码框（或收起面板）就会按这份结果重建。
      </p>
    </section>

    <!-- ── 代码 ────────────────────────────────────────────── -->
    <section class="np-jsset__card">
      <header class="np-jsset__head">
        <h5 class="np-jsset__title">代码</h5>
        <span class="np-jsset__spacer" />
        <!--
          ⚠️ 这句是**给用户的定心丸**：上一版改完代码会被换回默认，
          所以这里明确说"已经存下来了"（写的是真的：input 事件就落文档）。
        -->
        <span class="np-jsset__saved" data-np-js-saved>改动即时保存</span>
      </header>

      <textarea
        class="np-jsset__code"
        data-np-js-code
        spellcheck="false"
        autocapitalize="off"
        autocomplete="off"
        wrap="off"
        rows="10"
        :value="code"
        @input="onCodeInput"
        @change="onCodeChange"
        @keydown="onCodeKeydown"
      />
      <p class="np-jsset__note">
        只在<strong>运行</strong>时执行（设计期不跑）。同步代码 ——
        <code>await</code> / 网络请求请用副作用节点。
      </p>
    </section>
  </div>
</template>

<!--
  ⚠️ 这份样式是**新写的**（2027）：面板原来一个 CSS 类都没有 ——
  十个原生控件堆在一起，和周围的深色界面完全不搭（用户原话："特别丑"）。
  颜色一律走主题令牌，不写死色值，这样换主题时它跟着走。
-->
<style scoped>
.np-jsset {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
}

.np-jsset__lead {
  margin: 0;
  color: var(--np-panel-mute);
  font-size: 10px;
  line-height: 1.6;
}

.np-jsset__lead code,
.np-jsset__note code {
  padding: 0 3px;
  border-radius: 3px;
  background: var(--np-panel-chip-bg);
  color: var(--np-panel-text);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 9.5px;
}

/* ── 一张卡片 = 一段设置 ── */

.np-jsset__card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 7px 8px 8px;
  border: 1px solid var(--np-border-soft);
  border-radius: var(--np-radius-m);
  background: var(--np-surface-1);
}

.np-jsset__head {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 18px;
}

.np-jsset__title {
  margin: 0;
  color: var(--np-panel-dim);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.np-jsset__count {
  min-width: 15px;
  padding: 0 4px;
  border-radius: var(--np-radius-s);
  background: var(--np-panel-chip-bg);
  color: var(--np-panel-text);
  font-size: 9.5px;
  font-variant-numeric: tabular-nums;
  text-align: center;
}

.np-jsset__spacer {
  flex: 1 1 auto;
}

.np-jsset__tag,
.np-jsset__saved {
  font-size: 9px;
  white-space: nowrap;
}

.np-jsset__tag {
  color: var(--np-panel-mute);
}

/* 「改动即时保存」—— 强调色，因为它是这一版修的那个 bug 的定心丸 */
.np-jsset__saved {
  color: var(--np-accent);
}

/* ── 按钮 ── */

.np-jsset__add {
  padding: 0 7px;
  height: var(--np-ctl-h-sm);
  border: 1px solid var(--np-border-strong);
  border-radius: var(--np-radius-s);
  background: var(--np-panel-input-bg);
  color: var(--np-panel-text);
  font: inherit;
  font-size: 10px;
  line-height: 1;
  cursor: pointer;
}

.np-jsset__add:hover {
  border-color: var(--np-accent);
  color: var(--np-accent);
}

.np-jsset__icon {
  flex: 0 0 auto;
  width: 18px;
  height: var(--np-ctl-h-sm);
  padding: 0;
  border: 1px solid transparent;
  border-radius: var(--np-radius-s);
  background: transparent;
  color: var(--np-panel-mute);
  font: inherit;
  font-size: 10px;
  line-height: 1;
  cursor: pointer;
}

.np-jsset__icon:hover {
  border-color: var(--np-danger, #d96a6a);
  color: var(--np-danger, #d96a6a);
}

/* ── 一路输入 ── */

.np-jsset__list,
.np-jsset__outs {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.np-jsset__port {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 5px;
  border: 1px solid transparent;
  border-left: 2px solid var(--np-border-strong);
  border-radius: var(--np-radius-s);
  background: var(--np-surface-0);
}

/* 变参行：左边那条竖线换成强调色 —— 一眼看出它是一组而不是一路 */
.np-jsset__port.is-variadic {
  border-left-color: var(--np-accent);
}

.np-jsset__port.is-bad {
  border-color: var(--np-danger, #d96a6a);
}

.np-jsset__port-main,
.np-jsset__port-sub {
  display: flex;
  align-items: center;
  gap: 4px;
}

.np-jsset__name {
  flex: 1 1 auto;
  min-width: 0;
  height: var(--np-ctl-h-sm);
  padding: 0 6px;
  border: 1px solid var(--np-border-strong);
  border-radius: var(--np-radius-s);
  background: var(--np-panel-input-bg);
  color: var(--np-panel-text);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 10.5px;
  outline: none;
}

.np-jsset__name:focus {
  border-color: var(--np-accent);
}

.np-jsset__port.is-bad .np-jsset__name {
  border-color: var(--np-danger, #d96a6a);
}

.np-jsset__select {
  flex: 0 0 auto;
  height: var(--np-ctl-h-sm);
  padding: 0 4px;
  border: 1px solid var(--np-border-strong);
  border-radius: var(--np-radius-s);
  background: var(--np-panel-input-bg);
  color: var(--np-panel-text);
  font: inherit;
  font-size: 10px;
  outline: none;
  cursor: pointer;
}

.np-jsset__select:focus {
  border-color: var(--np-accent);
}

.np-jsset__mode {
  flex: 1 1 auto;
  height: var(--np-ctl-h-sm);
  padding: 0 6px;
  border: 1px solid var(--np-border-strong);
  border-radius: var(--np-radius-s);
  background: transparent;
  color: var(--np-panel-mute);
  font: inherit;
  font-size: 10px;
  line-height: 1;
  cursor: pointer;
}

.np-jsset__mode:hover {
  color: var(--np-panel-text);
}

.np-jsset__mode.is-on {
  border-color: var(--np-accent);
  background: var(--np-accent-soft);
  color: var(--np-accent);
}

/* ── 输出（只读）── */

.np-jsset__out {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 3px 6px;
  border-radius: var(--np-radius-s);
  background: var(--np-surface-0);
}

.np-jsset__out-name {
  min-width: 0;
  overflow: hidden;
  color: var(--np-panel-text);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 10.5px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.np-jsset__out-kind {
  flex: 0 0 auto;
  color: var(--np-panel-mute);
  font-size: 9px;
}

/* ── 说明 / 报错 ── */

.np-jsset__note {
  margin: 0;
  color: var(--np-panel-mute);
  font-size: 10px;
  line-height: 1.6;
}

.np-jsset__problem,
.np-jsset__pending {
  margin: 0;
  padding: 4px 6px;
  border-radius: var(--np-radius-s);
  font-size: 10px;
  line-height: 1.5;
}

.np-jsset__problem {
  background: color-mix(in srgb, var(--np-danger, #d96a6a) 16%, transparent);
  color: var(--np-danger, #d96a6a);
}

.np-jsset__pending {
  background: var(--np-accent-soft);
  color: var(--np-accent);
}

/* ── 代码框 ── */

.np-jsset__code {
  width: 100%;
  min-height: calc(var(--np-ctl-h-md) * 6);
  max-height: 46vh;
  padding: 6px 7px;
  border: 1px solid var(--np-border-strong);
  border-radius: var(--np-radius-s);
  background: var(--np-panel-input-bg);
  color: var(--np-panel-text);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 10.5px;
  line-height: 1.55;
  /* ⚠️ 不折行：代码一折行，缩进就全乱了（同 NpWidgetCode 的理由） */
  white-space: pre;
  overflow: auto;
  resize: vertical;
  outline: none;
  tab-size: 2;
}

.np-jsset__code:focus {
  border-color: var(--np-accent);
}
</style>
